/**
 * NotificationsBell — lightweight community notifications dropdown.
 */

import React from 'react';
import { Bell, MessageCircle, AtSign, MessagesSquare, Calendar, UserCheck, Megaphone } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { cx } from '@/components/site/primitives';
import { useCommunities } from './context/CommunitiesContext';
import { timeAgo } from './utils/communityHelpers';
import EmptyState from './EmptyState';

const ICONS = {
  reply: MessageCircle, mention: AtSign, discussion: MessagesSquare, event: Calendar, join_accepted: UserCheck, announcement: Megaphone,
};

export default function NotificationsBell() {
  const { notifications, unreadNotificationCount, markNotificationsRead } = useCommunities();

  return (
    <DropdownMenu onOpenChange={(open) => open && markNotificationsRead()}>
      <DropdownMenuTrigger asChild>
        <button type="button" className="relative grid h-10 w-10 shrink-0 place-items-center rounded-full border border-line bg-card text-subtle transition-colors hover:border-coral/40 hover:text-ink" aria-label="Notifications">
          <Bell className="h-4 w-4" />
          {unreadNotificationCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 grid h-4 w-4 place-items-center rounded-full bg-coral text-[10px] font-bold text-[#FFF7F0]">
              {unreadNotificationCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 rounded-2xl border-line bg-card p-2 text-ink shadow-xl">
        <p className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-subtle">Notifications</p>
        <DropdownMenuSeparator className="bg-line" />
        {notifications.length === 0 ? (
          <div className="p-2"><EmptyState type="notifications" size="sm" /></div>
        ) : (
          <div className="max-h-80 space-y-0.5 overflow-y-auto py-1">
            {notifications.map(n => {
              const Icon = ICONS[n.type] ?? MessageCircle;
              return (
                <div key={n.id} className={cx('flex items-start gap-2.5 rounded-xl px-2 py-2.5', n.unread && 'bg-coral/5')}>
                  <span className={cx('mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full', n.unread ? 'bg-coral/15 text-coral' : 'bg-cream text-subtle')}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs leading-snug text-ink">{n.text}</p>
                    <p className="mt-0.5 text-[10px] text-subtle">{timeAgo(n.createdAt)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
